/**
 * Share / copy-URL dialog — explicit copy interaction (not a silent clipboard write).
 */

export type ShareModalOptions = {
  /** Full URL to share (already written to location if desired). */
  url: string;
  title?: string;
};

export class ShareModal {
  private readonly root: HTMLDialogElement;
  private readonly input: HTMLInputElement;
  private readonly status: HTMLElement;
  private readonly copyBtn: HTMLButtonElement;

  constructor() {
    const dlg = document.createElement("dialog");
    dlg.className = "share-dialog";
    dlg.setAttribute("aria-labelledby", "share-dialog-title");

    dlg.innerHTML = `
      <form method="dialog" class="share-dialog-inner">
        <header class="share-dialog-head">
          <h2 id="share-dialog-title">Copy link</h2>
          <button type="submit" value="cancel" class="btn share-close" aria-label="Close">×</button>
        </header>
        <p class="share-dialog-blurb">
          This URL restores the current preset, parameters, and playback settings.
        </p>
        <label class="share-url-label" for="share-url-input">Link</label>
        <div class="share-url-row">
          <input id="share-url-input" class="share-url-input" type="text" readonly spellcheck="false" />
          <button type="button" class="btn primary" id="share-copy-btn">Copy</button>
        </div>
        <p class="share-status" id="share-status" role="status" aria-live="polite"></p>
      </form>
    `;

    document.body.appendChild(dlg);
    this.root = dlg;
    this.input = dlg.querySelector("#share-url-input") as HTMLInputElement;
    this.status = dlg.querySelector("#share-status") as HTMLElement;
    this.copyBtn = dlg.querySelector("#share-copy-btn") as HTMLButtonElement;

    this.copyBtn.addEventListener("click", () => {
      void this.copy();
    });

    dlg.addEventListener("click", (ev) => {
      // Click backdrop closes
      if (ev.target === dlg) dlg.close();
    });

    dlg.addEventListener("close", () => {
      this.status.textContent = "";
      this.copyBtn.textContent = "Copy";
    });
  }

  open(opts: ShareModalOptions): void {
    this.input.value = opts.url;
    this.status.textContent = "";
    this.copyBtn.textContent = "Copy";
    if (!this.root.open) this.root.showModal();
    // Select full URL for keyboard users who prefer ⌘C
    requestAnimationFrame(() => {
      this.input.focus();
      this.input.select();
    });
  }

  close(): void {
    if (this.root.open) this.root.close();
  }

  private async copy(): Promise<void> {
    const url = this.input.value;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        this.input.focus();
        this.input.select();
        const ok = document.execCommand("copy");
        if (!ok) throw new Error("copy failed");
      }
      this.status.textContent = "Copied to clipboard.";
      this.copyBtn.textContent = "Copied";
      window.setTimeout(() => {
        if (this.copyBtn.textContent === "Copied")
          this.copyBtn.textContent = "Copy";
      }, 1600);
    } catch {
      this.status.textContent =
        "Could not copy automatically — select the link and copy manually.";
      this.input.focus();
      this.input.select();
    }
  }
}

let singleton: ShareModal | null = null;

export function getShareModal(): ShareModal {
  if (!singleton) singleton = new ShareModal();
  return singleton;
}

/** Build share URL string from current location after state write. */
export function currentShareUrl(): string {
  return location.href;
}
